import cn from 'classnames'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import s from './Layout.module.scss'
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { useAuthUI } from '@corratech/pylot-auth-manager'
import { Navbar } from '@airglobal/air-ooka-header'
import { useAcceptCookies } from '@corratech/pylot-utils'
import { Button } from '@corratech/pylot-ui'
import { LoadingIndicator } from '@components/LoadingIndicator'
import { UseZohoHideShow } from '@components/Zoho/hooks/useZohoHideShow'
import formStyles from '@commonStyles/Forms.module.scss'
import { useTranslation } from 'next-i18next'
import { AuthWrapper } from '@components/common/AuthWrapper'
import { useRouter } from 'next/router'
import { validate as EmailValidate } from 'email-validator'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'
import Cookies from 'js-cookie'
import { useScrolledPosition } from '@corratech/air-ooka-header/hooks/useScrolledPosition'

const dynamicProps = {
    // eslint-disable-next-line react/display-name
    loading: () => <LoadingIndicator />
}
const LoginView = dynamic(
    () => import('@components/alfakher/pylot-auth/src/LoginView'),
    { ...dynamicProps }
)

const SignUpView = dynamic(
    () => import('@components/alfakher/pylot-auth/src/SignUpView'),
    { ...dynamicProps }
)
const ForgotPassword = dynamic(
    () => import('@components/alfakher/pylot-auth/src/ForgotPassword'),
    { ...dynamicProps }
)

const ResetPassword = dynamic(
    () => import('@components/alfakher/pylot-auth/src/ResetPassword'),
    { ...dynamicProps }
)

const FeatureBar = dynamic(
    () => import('stores/ooka_usa_store/components/common/FeatureBar'),
    {
        ...dynamicProps
    }
)

const Footer = dynamic(() => import('@components/common/Footer'), {
    ssr: false
})

const Sidebar = dynamic(
    () =>
        import('stores/ooka_usa_store/components/alfakher/pylot-ui/src/Sidebar')
)

const CartSidebarView = dynamic(
    () => import('@components/alfakher/pylot-cart/CartSidebarView')
)

const Modal = dynamic(
    () =>
        import(
            'stores/ooka_usa_store/components/alfakher/pylot-ui/src/AccountModal'
        )
)

interface Props {
    authRequired: boolean
    pageProps: any
    setIsLoader?: Dispatch<SetStateAction<boolean>>
}

export enum MODAL_VIEW_ENUMS {
    LOGIN_VIEW = 'LOGIN_VIEW',
    SIGNUP_VIEW = 'SIGNUP_VIEW',
    FORGOT_VIEW = 'FORGOT_VIEW',
    RESET_VIEW = 'PASSWORD_RESET_VIEW'
}

export enum MODAL_HASH_ENUMS {
    LOGIN_VIEW = 'show-login-form',
    SIGNUP_VIEW = 'show-signup-form',
    FORGOT_VIEW = 'show-forgot-password',
    RESET_VIEW = 'show-reset-form',
    GUEST = 'session-expired'
}
const privacyLink = '/content/privacy-policy'

const Layout: FC<Props> = ({ children, authRequired, setIsLoader }) => {
    const { displaySidebar, closeSidebar, openToast } = useUI()
    const [isMobileMenuOpened, setIsMobileMenuOpened] = useState(false)
    const { setIsCookiesAccepted, pageType, setPageType } = useAlfakherContext()
    const [scrolledClass] = useScrolledPosition()
    const COOKIE_NAME = 'accept_cookies'
    const {
        modalView,
        displayModal,
        closeAuthModal,
        setModalView,
        openAuthModal
    } = useAuthUI()
    UseZohoHideShow()

    const { acceptedCookies, onAcceptCookies } = useAcceptCookies()

    const { t } = useTranslation('common')
    const [isHashChanged, setIsHashChanged] = useState<boolean>(false)

    const router = useRouter()

    // used to disable transparent header when hit other pages
    useEffect(() => {
        if (
            !router.asPath.includes('/c/') ||
            router.asPath.includes('/content/') ||
            !router.asPath.includes('/')
        ) {
            setPageType({
                transparentHeader: false,
                isNicotinePage: true
            })
        }
    }, [router.asPath])

    useEffect(() => {
        if (!Cookies.get(COOKIE_NAME)) {
            setIsCookiesAccepted(false)
        }
    }, [])

    useEffect(() => {
        if (acceptedCookies) {
            document.body.classList.add('cookie-accepted')
        } else {
            document.body.classList.remove('cookie-accepted')
        }
    }, [acceptedCookies])

    const onHashChangeComplete = (): void => {
        if (typeof window != 'undefined' && window.location.hash) {
            switch (window.location.hash.replace('#', '')) {
                case MODAL_HASH_ENUMS.LOGIN_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.LOGIN_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.SIGNUP_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.SIGNUP_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.FORGOT_VIEW:
                    setModalView(MODAL_VIEW_ENUMS.FORGOT_VIEW)
                    setIsHashChanged(true)
                    break
                case MODAL_HASH_ENUMS.RESET_VIEW:
                    {
                        const url = new URL(router.asPath, location.origin)
                        const email = url.searchParams.get('email')
                        const token = url.searchParams.get('token')

                        if (email && token && EmailValidate(email)) {
                            setModalView(MODAL_VIEW_ENUMS.RESET_VIEW, {
                                email: email,
                                token: token
                            })
                            setIsHashChanged(true)
                        }
                    }
                    break
                case MODAL_HASH_ENUMS.GUEST:
                    if (window?.localStorage.getItem('is_authorised') === '1') {
                        openToast(
                            t('Your session expired. Please sign in again.'),
                            ToastType.Warning
                        )
                        router.push('/')
                    } else if (window?.localStorage.getItem('pylot_token')) {
                        history.replaceState(null, '', window.location.pathname)
                    }
                    break
            }
        }
    }

    useEffect(() => {
        onHashChangeComplete()
        window.addEventListener('hashchange', onHashChangeComplete, false)
        return () => {
            window.removeEventListener(
                'hashchange',
                onHashChangeComplete,
                false
            )
        }
    }, [])

    useEffect(() => {
        if (isHashChanged === true) {
            openAuthModal()
            setIsHashChanged(false)
        }
    }, [isHashChanged])

    /**
     * Remove hash when popup is closed
     */
    const closeModal = () => {
        closeAuthModal()
        if (window.location.hash) {
            history.replaceState(null, '', ' ')
        }
    }

    const className = cn('flex md:block !px-3 !md:px-0 !py-2', {
        'banner-hidden': acceptedCookies
    })
    const onAcceptCookieshandler = () => {
        onAcceptCookies()
        setIsCookiesAccepted(true)
    }

    return (
        <div
            className={cn(
                s.root,
                `store_${router.locale}`,
                scrolledClass,
                isMobileMenuOpened && 'isMobileMenuOpened',
                {
                    transparentHeader: pageType?.transparentHeader,
                    notNicotinePage: !pageType?.isNicotinePage
                }
            )}
            id="layout-main"
        >
            <Navbar
                setIsMobileMenuOpened={setIsMobileMenuOpened}
                isMobileMenuOpened={isMobileMenuOpened}
            />
            <main className={cn(s['main-container'])} id="contentarea">
                <AuthWrapper authRequired={authRequired}>
                    {children}
                </AuthWrapper>
            </main>
            <Footer />
            {displayModal && (
                <Modal open={displayModal} onClose={closeModal}>
                    <div className={cn(formStyles.root)}>
                        {modalView === MODAL_VIEW_ENUMS.LOGIN_VIEW && (
                            <LoginView />
                        )}
                        {modalView === MODAL_VIEW_ENUMS.SIGNUP_VIEW && (
                            <SignUpView />
                        )}
                        {modalView === MODAL_VIEW_ENUMS.FORGOT_VIEW && (
                            <ForgotPassword />
                        )}

                        {modalView === MODAL_VIEW_ENUMS.RESET_VIEW && (
                            <ResetPassword />
                        )}
                    </div>
                </Modal>
            )}
            {displaySidebar && (
                <Sidebar open={displaySidebar} onClose={closeSidebar}>
                    <CartSidebarView />
                </Sidebar>
            )}
            <FeatureBar
                className={className}
                title={
                    <div className={s['ooka_usa_cookie_text']}>
                        {t('By using this site you agree to our')}{' '}
                        <Link href={privacyLink}>{t('Cookies Policy')}</Link>{' '}
                    </div>
                }
                hide={acceptedCookies}
                action={
                    <Button onClick={() => onAcceptCookieshandler()}>
                        <span className={s['cookie-button']}>{t('Ok')}</span>
                    </Button>
                }
            />
        </div>
    )
}

export default Layout
